import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useReveal } from '@/hooks/useScrollAnimation';

export default function MenuSection() {
  const [categories, setCategories] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const { lang, t } = useLanguage();
  const [headerRef, headerVisible] = useReveal(0.1);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await publicApi.getCategories();
        setCategories(res.data || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };

    fetchCategories();
  }, []);

  const displayCategories = categories
    .filter((cat) => cat.id !== 'bols')
    .map((cat) => {
      if (cat.id === 'rouleaux') {
        return {
          ...cat,
          name_fr: 'Rouleaux & Bols',
          name_en: 'Spring Rolls & Bowls',
        };
      }

      return cat;
    });

  const activeCat = displayCategories[activeIndex];

  const getImage = (cat) =>
    lang === 'en' && cat?.image_url_en ? cat.image_url_en : cat?.image_url;

  const activeImage = getImage(activeCat);

  const goPrev = () => {
    if (displayCategories.length === 0) return;

    setActiveIndex((prev) =>
      prev === 0 ? displayCategories.length - 1 : prev - 1
    );
  };

  const goNext = () => {
    if (displayCategories.length === 0) return;

    setActiveIndex((prev) =>
      prev === displayCategories.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="py-20 md:py-24 bg-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={headerRef}
          className={`reveal mb-12 ${headerVisible ? 'visible' : ''}`}
        >
          <p
            className="text-sm tracking-[0.3em] uppercase text-[#FFD700] mb-4 font-medium"
            style={{ fontFamily: 'Manrope' }}
          >
            {t('menu.sectionLabel')}
          </p>

          <h2
            data-testid="menu-heading"
            className="text-4xl md:text-5xl lg:text-6xl tracking-tight text-white"
          >
            {t('menu.sectionTitle')}
          </h2>
        </div>

        <div className="flex flex-wrap gap-4 md:gap-6 mb-10">
          {displayCategories.map((cat, index) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`text-sm md:text-base tracking-[0.2em] uppercase font-bold pb-2 transition-colors ${
                activeIndex === index
                  ? 'text-[#FFD700]'
                  : 'text-[#B0B0B0] hover:text-white'
              }`}
              style={{ fontFamily: 'Bebas Neue' }}
            >
              {lang === 'fr' ? cat.name_fr : cat.name_en}
            </button>
          ))}
        </div>

        {activeImage && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="w-full max-w-4xl overflow-hidden rounded-3xl border border-[#222] bg-[#0A0A0A] p-2 md:p-3 shadow-2xl cursor-zoom-in transition-all duration-500 hover:border-[#FFD700]/40 hover:shadow-[0_0_60px_rgba(255,215,0,0.12)]"
            >
              <img
                src={activeImage}
                alt={lang === 'fr' ? activeCat?.name_fr : activeCat?.name_en}
                className="w-full max-h-[70vh] object-contain rounded-2xl"
                loading="lazy"
              />
            </button>
          </div>
        )}
      </div>

      {lightboxOpen && activeImage && (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-xl flex items-center justify-center px-4 py-6">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,215,0,0.12),transparent_45%)] pointer-events-none" />

          <div className="absolute top-5 left-5 right-5 z-10 flex items-center justify-between">
            <div className="rounded-full border border-white/10 bg-black/40 backdrop-blur-md px-4 py-2 text-xs md:text-sm text-white/80 tracking-widest uppercase">
              {lang === 'fr' ? activeCat?.name_fr : activeCat?.name_en}
            </div>

            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="rounded-full border border-white/10 bg-black/40 backdrop-blur-md p-3 text-white hover:text-[#FFD700] hover:border-[#FFD700]/40 transition-all"
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>

          <button
            type="button"
            onClick={goPrev}
            className="absolute left-4 md:left-8 z-10 rounded-full border border-white/10 bg-black/40 backdrop-blur-md p-3 md:p-4 text-white hover:text-[#FFD700] hover:border-[#FFD700]/40 transition-all"
            aria-label="Previous menu"
          >
            <ChevronLeft size={30} />
          </button>

          <div className="relative z-0 max-w-[94vw] max-h-[88vh] rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-2 md:p-3 shadow-[0_30px_100px_rgba(0,0,0,0.65)]">
            <img
              src={activeImage}
              alt={lang === 'fr' ? activeCat?.name_fr : activeCat?.name_en}
              className="max-w-[90vw] max-h-[84vh] object-contain rounded-2xl"
            />
          </div>

          <button
            type="button"
            onClick={goNext}
            className="absolute right-4 md:right-8 z-10 rounded-full border border-white/10 bg-black/40 backdrop-blur-md p-3 md:p-4 text-white hover:text-[#FFD700] hover:border-[#FFD700]/40 transition-all"
            aria-label="Next menu"
          >
            <ChevronRight size={30} />
          </button>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full border border-white/10 bg-black/40 backdrop-blur-md px-4 py-2 text-[10px] md:text-xs text-white/60 tracking-widest uppercase">
            Click × to close
          </div>
        </div>
      )}
    </section>
  );
}